/*
 * Created on Wed Nov 01 2023 4:27:51 PM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023 
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */
import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { updateCartProducts } from "./api/action/cart.jsx";
import { getOrderById } from "./api/action/order.jsx";
import "./App.css";
import "./assets/css/cover.css";
import "./assets/css/features.css";
import "./styles/flexOfficeMobileApp.css";
import SiteStartupScreen from "./components/SiteStartupScreen.jsx";
import MainRoutes from "./container/MainRoutes";
import CartProvider from "./context/CartContext";
import { ThemeProvider } from "./context/ThemeContext.jsx";
import NativeStatusBarSync from "./components/NativeStatusBarSync.jsx";
import { KIOSK_HOME_ROUTE, KIOSK_SESSION_FLAG_KEY, KIOSK_SESSION_STARTED_AT_KEY } from "./domains/flexOffice/kiosk/kioskConfig.js";
import { isKioskSessionActive, verifyKioskPin } from "./domains/flexOffice/kiosk/kioskSession.js";
import { UserProvider } from "./context/UserContext";
import { SiteContextProvider } from "./context/SiteContext.jsx";
import { DomainLoading } from "./domain/Domain.jsx";
import {
  clearLegacySiteContextKeys,
  getSiteContextCache,
  isAssignedSiteContext,
  markSiteContextRevalidating,
  normalizeSiteHost,
  writeLegacySiteContextBridge
} from "./site/siteContext";
import { resolveSiteContext } from "./site/resolveSiteContext";
import { logout } from "./api/action/user.jsx";
import { useAuthSession } from "./auth/useAuthSession.js";
import { isAuthEntryPath } from "./utils/pendingNavigation.js";
import { getUnsavedChangesGuard } from "./utils/unsavedChangesGuardRegistry";

// Lazy-loaded: the template editor's preview pulls in the large canvas-engine
// but is only ever hit on the standalone /template-preview route, so it's kept
// out of the main app bundle every regular visitor downloads.
const TemplatePreview = lazy(() => import("./template/TemplatePreview.jsx"));

const cleanupPaymentData = () => {
  const paymentKeys = [
    "applicantFeePayment",
    "applicantUserId",
    "applicantName",
    "applicantTotalAmount",
    "applicantOrderId",
    "invoiceprimaryId",
    "orderDetails",
    "persistedOrder",
    "cartProducts",
    "shoppingCartData"
  ];

  paymentKeys.forEach(key => {
    localStorage.removeItem(key);
  });

  sessionStorage.removeItem("tempPaymentData");
};

const getOrderRecords = (orderValue) => {
  if (!orderValue) return [];
  if (Array.isArray(orderValue)) return orderValue.filter(Boolean);
  if (Array.isArray(orderValue.orderDetails)) return orderValue.orderDetails.filter(Boolean);
  if (Array.isArray(orderValue.childOrders)) return orderValue.childOrders.filter(Boolean);
  return [orderValue];
};

const getOrderLineItems = (order = {}) => {
  if (Array.isArray(order.lineItemList) && order.lineItemList.length > 0) {
    return order.lineItemList.filter(Boolean);
  }
  if (order.scheduledProduct || order.product) {
    return [{ scheduledProduct: order.scheduledProduct, product: order.product }];
  }
  return [];
};

const preserveSuccessfulEducationPaymentEnrollments = () => {
  if (localStorage.getItem("domainType") !== "EDUCATION") return;

  try {
    const storedOrderDetails = JSON.parse(localStorage.getItem("orderDetails") || "null");
    const persistedOrder = JSON.parse(localStorage.getItem("persistedOrder") || "null");
    const orders = [
      ...getOrderRecords(storedOrderDetails),
      ...getOrderRecords(persistedOrder)
    ];

    const pendingEnrollments = orders.flatMap((order) => (
      getOrderLineItems(order).map((lineItem) => {
        const scheduledProduct = lineItem?.scheduledProduct || (lineItem?.item?.product ? lineItem.item : null);
        const product = lineItem?.product || scheduledProduct?.product || lineItem?.item?.product || lineItem?.item || null;
        const id = product?.primaryId || scheduledProduct?.primaryId || lineItem?.primaryId;
        if (!id || !product) return null;

        return {
          id,
          product,
          scheduledProduct,
          orderDate: order?.createdDate || new Date().toISOString(),
          orderNumber: order?.number,
          type: "pending",
          status: "PURCHASE_IN_PROGRESS",
          pending: true,
        };
      }).filter(Boolean)
    ));

    if (!pendingEnrollments.length) return;

    const existing = JSON.parse(localStorage.getItem("pendingEnrollments") || "[]");
    const combined = [...(Array.isArray(existing) ? existing : []), ...pendingEnrollments];
    const deduped = combined.filter((item, index, list) => (
      list.findIndex((candidate) => String(candidate.id) === String(item.id)) === index
    ));
    localStorage.setItem("pendingEnrollments", JSON.stringify(deduped));
  } catch {
    // Ignore storage/parse issues; confirmed enrollment APIs remain authoritative.
  }
};

// Called from the payment-gateway return handling below (success AND
// failure paths) for every visitor, kiosk or not — sessionStorage.clear()
// was wiping the kiosk session flag (KIOSK_SESSION_FLAG_KEY) the instant a
// kiosk guest's card payment came back, which is exactly why kiosk mode was
// silently exiting to normal user mode right after a successful/failed
// payment. Preserving those two keys across the clear is a no-op for every
// non-kiosk visitor (the keys are simply absent), so normal behavior is
// unchanged.
const clearAllSessionStorage = () => {
  const kioskFlag = sessionStorage.getItem(KIOSK_SESSION_FLAG_KEY);
  const kioskStartedAt = sessionStorage.getItem(KIOSK_SESSION_STARTED_AT_KEY);
  sessionStorage.clear();
  if (kioskFlag) {
    sessionStorage.setItem(KIOSK_SESSION_FLAG_KEY, kioskFlag);
    if (kioskStartedAt) sessionStorage.setItem(KIOSK_SESSION_STARTED_AT_KEY, kioskStartedAt);
  }
};

const ADMIN_ROLE_TYPES = ["ADMIN", "SUPER_ADMIN", "INSTRUCTOR"];
const USER_FACING_ROLE_TYPES = ["CUSTOMER", "STUDENT"];
const USER_FACING_ROLE_BY_DOMAIN = {
  EDUCATION: "STUDENT",
  FLEX_OFFICE: "CUSTOMER",
  RETAIL: "CUSTOMER",
  SERVICES: "CUSTOMER"
};
const EMPTY_OBJECT = {};

const getDomainUserRole = (domainType) => (
  USER_FACING_ROLE_BY_DOMAIN[String(domainType || "").toUpperCase()] || ""
);

const getDomainUserLabel = (domainType) => (
  getDomainUserRole(domainType) === "STUDENT" ? "Student" : "Customer"
);

const getDomainUserDescription = (domainType) => {
  switch (String(domainType || "").toUpperCase()) {
    case "EDUCATION":
      return "Continue to your academy dashboard";
    case "RETAIL":
      return "Continue to the store portal";
    default:
      return "Browse and book office spaces";
  }
};

const getStoredUserRoles = () => {
  const rawRoles = localStorage.getItem("userRoles");

  if (!rawRoles) {
    return [];
  }

  try {
    const parsedRoles = JSON.parse(rawRoles);
    const roles = Array.isArray(parsedRoles) ? parsedRoles : [parsedRoles];
    return [...new Set(roles.map((role) => String(role || "").toUpperCase()).filter(Boolean))];
  } catch {
    return [...new Set(rawRoles.split(",").map((role) => String(role || "").trim().toUpperCase()).filter(Boolean))];
  }
};

const isLocalOrManageBiz4uUrl = () => {
  const { hostname = "", href = "" } = window.location;
  const normalizedHostname = hostname.toLowerCase();
  const normalizedHref = href.toLowerCase();

  const isLocalhost = [
    "localhost",
    "127.0.0.1",
    "0.0.0.0"
  ].includes(normalizedHostname);

  const isPrivateNetworkHost = normalizedHostname.startsWith("192.168.")
    || normalizedHostname.startsWith("10.")
    || normalizedHostname.startsWith("172.16.");

  return isLocalhost || isPrivateNetworkHost || normalizedHref.includes("managebiz4u");
};

// The Flex Office native shell (Capacitor) serves the bundle from a webview origin
// (capacitor://localhost / http://localhost) that the company-lookup API can't resolve,
// so it substitutes the configured Flex Office production host instead of deriving the
// subdomain from window.location. Web behavior is unchanged.
const isNativeMobileApp = () => Boolean(window?.Capacitor?.isNativePlatform?.());

const getEffectiveSubdomain = () => {
  if (isNativeMobileApp() && process.env.REACT_APP_FLEX_OFFICE_HOST) {
    return process.env.REACT_APP_FLEX_OFFICE_HOST;
  }

  const fullUrl = window?.location?.href || "";
  const urlParts = fullUrl.split("/");
  return urlParts.length >= 3 ? `${urlParts[0]}//${urlParts[2]}` : fullUrl;
};

function App() {
  const isTemplatePreviewRoute = typeof window !== "undefined" && window.location.pathname === "/template-preview";
  const [alertMsg, setAlertMsg] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const userData = null;
  const [domainStatus, setDomainStatus] = useState("loading");
  const [siteContext, setSiteContext] = useState(null);
  const [showRoleChoiceModal, setShowRoleChoiceModal] = useState(false);
  const [showKioskEntryPrompt, setShowKioskEntryPrompt] = useState(false);
  const [kioskEntryPin, setKioskEntryPin] = useState("");
  const [kioskEntryError, setKioskEntryError] = useState("");
  const adminRedirectInProgressRef = useRef(false);
  const roleChoiceResolvedRef = useRef(false);
  const lastBackPressRef = useRef(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, isLoggedIn, roles } = useAuthSession();

  // The admin login handoff (Login.jsx `redirectToConsole`) does a hard
  // `window.location.assign` to admin-console-ui, leaving this page's last
  // committed render (e.g. AuthPageReader's "Opening console access..."
  // redirect state, or a blank mid-transition frame) frozen in Chrome's
  // back-forward cache. Clicking Back in the console then restores that
  // frozen snapshot instead of re-running React's mount logic, which is why
  // the page can appear blank with stale branding (AA-3000). Reloading on a
  // bfcache restore forces a fresh mount so auth/company state is
  // re-evaluated and the page renders correctly again.
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        window.dispatchEvent(new Event("avps-auth-updated"));
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  useEffect(() => {
    const logoutCode = new URLSearchParams(window.location.search).get("code");
    const handleAuthExpired = () => {
      void dispatch(logout({ reason: "token-expired" }));
      navigate("/sign-in", { replace: true, state: { isTokenExpired: true } });
    };

    window.addEventListener("avps-auth-expired", handleAuthExpired);
    if (logoutCode === "avpslogout") {
      void dispatch(logout({ reason: "external-console-logout" }));
      navigate("/sign-in", { replace: true });
    }

    return () => {
      window.removeEventListener("avps-auth-expired", handleAuthExpired);
    };
  }, [dispatch, navigate]);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMsg(prev => ({ ...prev, open: false }));
  };

  useEffect(() => {
    if (!alertMsg.open) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setAlertMsg(prev => ({ ...prev, open: false }));
    }, 4000);

    return () => clearTimeout(timeoutId);
  }, [alertMsg.open]);

  const shouldDisableInspect = !isLocalOrManageBiz4uUrl();

  useEffect(() => {
    if (!shouldDisableInspect) return;

    const preventContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    const disableDevTools = (e) => {
      if (e.button === 2 || e.button === 3) {
        e.preventDefault();
        return false;
      }
    };

    const disableKeyboardShortcuts = (e) => {
      const key = String(e.key || "");

      if (key === 'F12') {
        e.preventDefault();
        return false;
      }

      if (e.ctrlKey && e.shiftKey && key === 'I') {
        e.preventDefault();
        return false;
      }

      if (e.ctrlKey && e.shiftKey && key === 'J') {
        e.preventDefault();
        return false;
      }

      if (e.ctrlKey && e.shiftKey && key === 'C') {
        e.preventDefault();
        return false;
      }

      if (e.ctrlKey && key === 'u') {
        e.preventDefault();
        return false;
      }

      if (e.ctrlKey && e.shiftKey && key === 'U') {
        e.preventDefault();
        return false;
      }

      if (e.ctrlKey && e.shiftKey && key === 'K') {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('contextmenu', preventContextMenu);
    document.addEventListener('mousedown', disableDevTools);
    document.addEventListener('mouseup', disableDevTools);
    document.addEventListener('keydown', disableKeyboardShortcuts);

    return () => {
      document.removeEventListener('contextmenu', preventContextMenu);
      document.removeEventListener('mousedown', disableDevTools);
      document.removeEventListener('mouseup', disableDevTools);
      document.removeEventListener('keydown', disableKeyboardShortcuts);
    };
  }, [shouldDisableInspect]);

  const authState = useSelector((state) => state?.auth?.data) || EMPTY_OBJECT;
  const companyState = useSelector((state) => state?.companyReducer?.company) || EMPTY_OBJECT;
  const currentDomain = String(siteContext?.domainType || "").toUpperCase();
  const currentDomainUserRole = getDomainUserRole(currentDomain);
  const currentDomainUserLabel = getDomainUserLabel(currentDomain);
  const currentDomainUserDescription = getDomainUserDescription(currentDomain);
  const userDetail = useSelector((state) => state?.auth?.data) || EMPTY_OBJECT;

  const companyPrimaryId = companyState?.primaryId || '';

  const getSuccessfulPaymentRedirectPath = useCallback(() => {
    if (currentDomain === "FLEX_OFFICE") return "/my-bookings";
    if (currentDomain === "EDUCATION") return "/myCourses";
    return "/";
  }, [currentDomain]);

  const getAdminConsoleUrl = useCallback(() => {
    const currentOrigin = window.location.origin;

    if (currentOrigin.includes(":3000")) {
      return `${currentOrigin.replace(":3000", ":4000")}/console/manage`;
    }

    return "/console/manage";
  }, []);

  const handleRoleChoice = useCallback((selectedRole) => {
    roleChoiceResolvedRef.current = true;
    setShowRoleChoiceModal(false);

    if (USER_FACING_ROLE_TYPES.includes(selectedRole)) {
      navigate(currentDomain === "FLEX_OFFICE" ? "/flex-home" : "/", { replace: true });

    } else if (selectedRole === "ADMIN") {
      adminRedirectInProgressRef.current = true;
      window.location.href = getAdminConsoleUrl();
    }
  }, [currentDomain, getAdminConsoleUrl, navigate]);

  const handleKioskEntrySubmit = useCallback((e) => {
    e.preventDefault();
    // Reuses the exact same PIN check + "verified" flag as manually opening
    // /kiosk (KioskPinGate.jsx) — verifyKioskPin is the single place that
    // constant is compared, so this shortcut and a direct URL visit can
    // never fall out of sync with each other.
    if (!verifyKioskPin(kioskEntryPin)) {
      setKioskEntryError("Incorrect PIN.");
      setKioskEntryPin("");
      return;
    }
    setShowKioskEntryPrompt(false);
    navigate('/kiosk', { replace: true });
  }, [kioskEntryPin, navigate]);

  useEffect(() => {
    const monitorAdminRoleSession = () => {
      if (adminRedirectInProgressRef.current) {
        return;
      }

      if (!(isAuthenticated || isLoggedIn)) {
        return;
      }

      const userRoles = roles.length > 0 ? roles : getStoredUserRoles();
      const hasAdminRole = userRoles.some((role) => ADMIN_ROLE_TYPES.includes(role));
      const hasUserFacingRole = currentDomainUserRole
        ? userRoles.includes(currentDomainUserRole)
        : userRoles.some((role) => USER_FACING_ROLE_TYPES.includes(role));

      if (!hasAdminRole) {
        roleChoiceResolvedRef.current = false;
        setShowRoleChoiceModal(false);
        return;
      }

      if (hasUserFacingRole) {
        if (!roleChoiceResolvedRef.current && isAuthEntryPath(window.location.pathname)) {
          setShowRoleChoiceModal(true);
        }
        return;
      }

      // An authenticated session must not replace a valid deep link. Role-based
      // dashboard routing is only a fallback when the tab opened on an auth entry page.
      if (!isAuthEntryPath(window.location.pathname)) {
        return;
      }

      const adminConsoleUrl = getAdminConsoleUrl();
      const currentUrl = `${window.location.origin}${window.location.pathname}`;

      if (currentUrl === adminConsoleUrl) {
        return;
      }

      adminRedirectInProgressRef.current = true;
      window.location.href = adminConsoleUrl;
    };

    monitorAdminRoleSession();

    // 50 ms polling fired 20×/s and saturated the main thread on every page.
    // Role data lives in localStorage — changes only happen at login/logout or
    // on a storage event from another tab. A 2 s fallback poll is more than
    // fast enough while eliminating the jank.
    const intervalId = setInterval(monitorAdminRoleSession, 2000);
    const handleStorage = () => monitorAdminRoleSession();
    const handleFocus = () => monitorAdminRoleSession();

    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", handleFocus);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", handleFocus);
    };
  }, [currentDomainUserRole, getAdminConsoleUrl, isAuthenticated, isLoggedIn, roles]);

  useEffect(() => {
    if (domainStatus !== "ready" || !siteContext || !isNativeMobileApp()) return;

    const resolvedDomainType = String(siteContext.domainType || "").toUpperCase();
    if (resolvedDomainType === "FLEX_OFFICE" && window.location.pathname === "/") {
      // Straight to the new native mobile shell (src/mobile/**), not the web
      // "/flex-home" page — AcademyRouteTree.jsx's isNativeMobileApp() branch
      // would redirect "/flex-home" here anyway, so going direct skips a hop.
      window.location.replace("/mobile/home");
    }
  }, [domainStatus, siteContext]);

  // Hide the native splash (launchAutoHide: false in capacitor.config.ts) once the
  // app has actually resolved into real content or a real error screen, instead of
  // it auto-hiding on cold start into a blank white screen while React/redux-persist
  // and domain resolution are still in flight.
  useEffect(() => {
    if (!isNativeMobileApp()) return;
    if (domainStatus !== "ready" && domainStatus !== "error") return;

    import('@capacitor/splash-screen').then(({ SplashScreen }) => {
      SplashScreen.hide();
    });
  }, [domainStatus]);

  // Hard safety net: domainStatus already resolves to "ready" or "error" within
  // ~5s (see the domain-check timeout above), but if some other unforeseen path
  // leaves it stuck (e.g. a thrown error before that effect runs), the installed
  // app must never sit on the native splash / a blank WebView indefinitely.
  // Force-hide once, log it so startup failures are diagnosable, and let
  // whatever screen is currently rendered (even a blank one) become visible
  // rather than staying hidden behind the splash forever.
  useEffect(() => {
    if (!isNativeMobileApp()) return undefined;

    const STARTUP_TIMEOUT_MS = 8000;
    const timeoutId = window.setTimeout(() => {
      if (domainStatus === "ready" || domainStatus === "error") return;
      // eslint-disable-next-line no-console
      console.error(`[startup] domainStatus still "${domainStatus}" after ${STARTUP_TIMEOUT_MS}ms — force-hiding splash screen to avoid an indefinite white/splash screen.`);
      import('@capacitor/splash-screen').then(({ SplashScreen }) => {
        SplashScreen.hide();
      });
    }, STARTUP_TIMEOUT_MS);

    return () => window.clearTimeout(timeoutId);
  }, [domainStatus]);

  useEffect(() => {
    const resolvedCompany = siteContext?.company || companyState;
    if (resolvedCompany?.name) {
      document.title = resolvedCompany.name;
    }

    if (siteContext?.branding?.faviconUrl || resolvedCompany?.logoImageContent?.displayLink) {
      const favicon = document.getElementById("favicon");
      if (favicon) {
        favicon.href = siteContext?.branding?.faviconUrl || resolvedCompany.logoImageContent.displayLink;
      }
    }
  }, [companyState, siteContext]);

  const resolveTenantSite = useCallback(async () => {
    if (isTemplatePreviewRoute) return;

    const requestUrl = getEffectiveSubdomain();
    const siteHost = normalizeSiteHost(requestUrl);
    if (!requestUrl || requestUrl === "file://" || !siteHost) {
      setSiteContext(null);
      setDomainStatus("error");
      return;
    }

    clearLegacySiteContextKeys();
    setSiteContext(null);
    setDomainStatus("loading");

    const cachedContext = getSiteContextCache(siteHost);
    const cachedAssignedContext = isAssignedSiteContext(cachedContext)
      ? markSiteContextRevalidating(cachedContext)
      : null;
    if (cachedAssignedContext) {
      setSiteContext(cachedAssignedContext);
      writeLegacySiteContextBridge(cachedAssignedContext);
      setDomainStatus("ready");
    }

    try {
      const { context } = await resolveSiteContext({
        dispatch,
        requestUrl,
        host: siteHost,
      });
      setSiteContext(context);
      writeLegacySiteContextBridge(context);
      localStorage.setItem("handleError", "false");
      setDomainStatus("ready");
    } catch {
      // The only permitted fallback is a context cached for this exact host.
      if (!cachedAssignedContext) {
        setSiteContext(null);
        setDomainStatus("error");
      }
    }
  }, [dispatch, isTemplatePreviewRoute]);

  useEffect(() => {
    resolveTenantSite();
  }, [resolveTenantSite]);

  const checkOrderDueAmount = useCallback(async (orderId) => {
    if (!orderId) {
      return null;
    }

    try {
      await new Promise((resolve) => setTimeout(resolve, 7000));
      const response = await dispatch(getOrderById(orderId));

      if (response?.dueAmount && response.dueAmount > 0) {
        const accountId = localStorage.getItem("accountId") || '';
        const shoppingCartId = localStorage.getItem("shoppingCartId") || '';
        const applicantId = localStorage.getItem("applicantId") || '';
        const totalAmount = localStorage.getItem("totalAmount") || '';

        const redirectUrl = `/admin/order-history/?accountId=${accountId}&shoppingCartId=${shoppingCartId}&applicantId=${applicantId}&orderId=${orderId}&amount=${response.dueAmount}&totalAmount=${totalAmount}&dueAmount=${response.dueAmount}&payBalanceAmount=hasBalance`;
        window.location.href = redirectUrl;
      }

      return response;
    } catch {
      return null;
    }
  }, [dispatch]);

  const navigateToCheckinCheckout = useCallback(() => {
    let guestPrimaryId = null;

    const guestData = sessionStorage.getItem("guestData");
    if (guestData) {
      try {
        const parsedGuestData = JSON.parse(guestData);
        guestPrimaryId = parsedGuestData?.email || null;
      } catch {
      }
    }

    if (!guestPrimaryId) {
      guestPrimaryId = localStorage.getItem("guestPrimaryId");
    }

    const baseUrl = window.location.origin;
    const checkinCheckoutUrl = `${baseUrl}/console/reservation/checkin-checkout${guestPrimaryId ? `?guestId=${guestPrimaryId}` : ''}`;

    window.location.href = checkinCheckoutUrl;
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window?.location?.search || "");
    const txnStatus = params.get("responseToken") || params.get("txnStatus") || (params.has("txn_success") ? "txn_success" : "");
    const razorpayStatus = params.get("razorpay_payment_link_status")

    if (!txnStatus) {
      return;
    }

    try {
      const subscriptionPaymentContext = JSON.parse(sessionStorage.getItem("subscriptionBillPayment:pending") || "{}") || {};
      if (subscriptionPaymentContext.invoiceId || subscriptionPaymentContext.orderId || subscriptionPaymentContext.billId) {
        window.location.replace(`/console/payment-status${window.location.search || ""}`);
        return;
      }
    } catch {
    }

    const handleSuccessResponse = async (response) => {
      const isApplicantPayment = localStorage.getItem("applicantFeePayment") === 'true';

      if (response === "Message published") {
        setAlertMsg({
          open: true,
          message: "Payment successful! Your payment has been successfully processed. Thank you!",
          severity: "success"
        });

        const orderId = localStorage.getItem("applicantOrderId");
        const shoppingCartId = authState?.account?.shoppingCart?.primaryId;
        const accountId = authState?.account?.primaryId;

        if (shoppingCartId && accountId) {
          const cartPayload = {
            primaryId: shoppingCartId,
            accountId: accountId,
            createdBy: userDetail?.firstName || "System",
            modifiedBy: userDetail?.lastName || "System",
            productList: [],
            company: { primaryId: companyPrimaryId || 0 }
          };

          try {
            await updateCartProducts(cartPayload);
          } catch {
          }
        }

        preserveSuccessfulEducationPaymentEnrollments();
        cleanupPaymentData();
        clearAllSessionStorage();

        const isGuest = localStorage.getItem("guestAuthenticated") === 'true';

        if (isGuest) {
          setTimeout(() => {
            navigateToCheckinCheckout();
          }, 5000);
        } else if (orderId) {
          const orderResponse = await checkOrderDueAmount(orderId);
          if (orderResponse?.dueAmount === 0) {
            setTimeout(() => {
              // Kiosk: back to the kiosk landing screen, never the normal
              // customer "My Bookings" page — see clearAllSessionStorage
              // above for why the kiosk session survives to this point now.
              window.location.href = isKioskSessionActive() ? KIOSK_HOME_ROUTE : getSuccessfulPaymentRedirectPath();
            }, 5000);
          }
        } else {
          setTimeout(() => {
            window.location.href = isKioskSessionActive() ? KIOSK_HOME_ROUTE : getSuccessfulPaymentRedirectPath();
          }, 5000);
        }
      } else {
        showErrorAndRedirect(isApplicantPayment);
      }
    };

    const showErrorAndRedirect = (isApplicantPayment) => {
      Swal.fire({
        icon: "error",
        title: "Failed to publish message",
        text: "Please try again.",
        showConfirmButton: false,
        timer: 5000,
      });

      const isGuest = localStorage.getItem("guestAuthenticated") === 'true';

      if (isGuest) {
        cleanupPaymentData();
        clearAllSessionStorage();
        setTimeout(() => {
          navigateToCheckinCheckout();
        }, 2000);
      } else if (isKioskSessionActive()) {
        setTimeout(() => {
          window.location.href = KIOSK_HOME_ROUTE;
        }, 2000);
      } else {
        setTimeout(() => {
          window.location.href = isApplicantPayment
            ? "/console/manage"
            : "/order-history";
        }, 2000);
      }
    };

    let isSuccess = false;
    let isFailure = false;

    if (txnStatus === "TXN_SUCCESS") {
      isSuccess = true;
    } else if (txnStatus === "TXN_FAILURE") {
      isFailure = true;
    }

    if (txnStatus === "RAZORPAY_RETURN") {
      if (razorpayStatus === "paid") {
        isSuccess = true;
      } else {
        isFailure = true;
      }
    }

    if (isSuccess) {
      handleSuccessResponse("Message published");

      if (
        localStorage.getItem("applicantFeePayment") === "true" &&
        !localStorage.getItem("applicantOrderId")
      ) {
        cleanupPaymentData();
        clearAllSessionStorage();
      }

    } else if (isFailure) {
      showErrorAndRedirect(false);
    }

  }, [
    authState?.account?.primaryId,
    authState?.account?.shoppingCart?.primaryId,
    checkOrderDueAmount,
    currentDomain,
    companyPrimaryId,
    getSuccessfulPaymentRedirectPath,
    navigateToCheckinCheckout,
    userDetail?.firstName,
    userDetail?.lastName
  ]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      const key = String(e.key || "").toLowerCase();
      const isS = key === 's';
      const isK = key === 'k';
      const target = e.target;
      const isTyping =
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable);

      if (isS && (e.altKey || e.metaKey)) {
        e.preventDefault();
        window.location.href = '/superadmin/sign-in';
        return;
      }

      // Kiosk mode shortcut: Alt+K (Windows/Linux) or Cmd+K (macOS). Active on both
      // web and the Capacitor build (attached-keyboard kiosk setups). Only opens
      // the PIN prompt below — kiosk mode itself is entered from there once the
      // correct PIN is confirmed.
      if (isK && (e.altKey || e.metaKey) && !isTyping) {
        e.preventDefault();
        setKioskEntryPin("");
        setKioskEntryError("");
        setShowKioskEntryPrompt(true);
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  // Marks <body> so App.css can apply safe-area-inset padding (see .capacitor-native
  // rule) only inside the native shell, keeping notched/gesture-nav devices from
  // rendering content under the status bar without affecting the web app.
  useEffect(() => {
    if (!isNativeMobileApp()) return undefined;

    document.body.classList.add('capacitor-native');
    return () => document.body.classList.remove('capacitor-native');
  }, []);

  // Android hardware/gesture back button (native shell only — no-op on web, where
  // @capacitor/app's listener API doesn't apply). Goes back through browser history
  // (what BrowserRouter's navigate()/Link push onto) and exits the app at the root
  // instead of leaving the WebView on a blank page, which is Capacitor's un-handled
  // default for single-page apps.
  //
  // Root detection is based on the current pathname (mobile Home), NOT
  // window.history.length: history.length is a WebView-session-global counter
  // that keeps growing across the whole app lifetime (login, redirects, every
  // screen ever visited) and is essentially always > 1 well before the user
  // is anywhere near the app's actual root — using it to gate "go back vs.
  // exit" meant every screen except the literal first-ever navigation could
  // wrongly fall into the exit-confirmation branch instead of going back, or
  // conversely pop through several unrelated prior screens (login, splash
  // redirects) before ever reaching the "press back again to exit" prompt,
  // which is what made back navigation feel like it was closing/resetting
  // the app instead of stepping back one mobile screen at a time.
  useEffect(() => {
    if (!isNativeMobileApp()) return undefined;

    let removeListener;
    let backNavigationInFlight = false;

    import('@capacitor/app').then(({ App: CapacitorApp }) => {
      CapacitorApp.addListener('backButton', () => {
        const guard = getUnsavedChangesGuard();
        if (guard?.isDirty?.()) {
          guard.requestBack();
          return;
        }

        // Ignore a second back-press that arrives while the previous one's
        // history.back() popstate hasn't finished processing yet (e.g. a
        // fast double back-press or a gesture fired twice).
        if (backNavigationInFlight) return;

        const path = window.location.pathname.toLowerCase().replace(/\/+$/, '') || '/';
        const isMobileHome = path === '/mobile/home' || path === '/mobile';

        if (!isMobileHome) {
          backNavigationInFlight = true;
          window.history.back();
          window.setTimeout(() => { backNavigationInFlight = false; }, 400);
          return;
        }

        // At the app's actual root (mobile Home): require a second
        // back-press within 2s before exiting, so a single accidental tap
        // doesn't close the app (standard Android "press back again to
        // exit" pattern).
        const now = Date.now();
        if (now - lastBackPressRef.current < 2000) {
          CapacitorApp.exitApp();
        } else {
          lastBackPressRef.current = now;
          Swal.fire({
            toast: true,
            position: 'bottom',
            showConfirmButton: false,
            timer: 2000,
            timerProgressBar: true,
            title: 'Press back again to exit',
          });
        }
      }).then((handle) => {
        removeListener = handle.remove;
      });
    });

    return () => {
      removeListener?.();
    };
  }, []);

  useEffect(() => {
    return () => {
      const temporaryKeys = [
        "tempOrderData",
        "tempPaymentData"
      ];

      temporaryKeys.forEach(key => {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
        }
      });
    };
  }, []);

  // The standalone console preview receives its complete company/template
  // payload through postMessage. It must not wait for hostname tenant
  // resolution, because the preview iframe is intentionally not a public site.
  if (isTemplatePreviewRoute) {
    return (
      <Suspense fallback={null}>
        <TemplatePreview />
      </Suspense>
    );
  }

  if (domainStatus === "loading") {
    return <SiteStartupScreen status="loading" />;
  }

  if (domainStatus === "error" || !siteContext) {
    return <SiteStartupScreen status="error" onRetry={resolveTenantSite} />;
  }
  const ToastContent = ({ message, severity, onClose }) => {
    const [progress, setProgress] = useState(100);
    const [visible, setVisible] = useState(false);
    const DURATION = 4000;

    useEffect(() => {
      const frame = requestAnimationFrame(() => setVisible(true));
      const start = Date.now();
      const interval = setInterval(() => {
        const elapsed = Date.now() - start;
        const remaining = Math.max(0, 100 - (elapsed / DURATION) * 100);
        setProgress(remaining);
        if (remaining <= 0) clearInterval(interval);
      }, 16);

      return () => {
        cancelAnimationFrame(frame);
        clearInterval(interval);
      };
    }, []);

    const config = {
      success: {
        bg: "linear-gradient(135deg, #059669, #047857)",
        glow: "rgba(5,150,105,0.35)",
        label: "Payment Successful",
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        ),
      },
      error: {
        bg: "linear-gradient(135deg, #dc2626, #b91c1c)",
        glow: "rgba(220,38,38,0.35)",
        label: "Something went wrong",
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4M12 16h.01" />
          </svg>
        ),
      },
      info: {
        bg: "linear-gradient(135deg, #2563eb, #1d4ed8)",
        glow: "rgba(37,99,235,0.35)",
        label: "Notice",
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4M12 8h.01" />
          </svg>
        ),
      },
    };

    const { bg, glow, label, icon } = config[severity] || config.info;

    return (
      <>
        {/* Keyframe styles injected once */}
        <style>{`
        @keyframes toastSlideIn {
          from { opacity: 0; transform: translateY(-24px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0)     scale(1);    }
        }
        @keyframes toastPop {
          0%   { transform: scale(1);    }
          40%  { transform: scale(1.06); }
          70%  { transform: scale(0.97); }
          100% { transform: scale(1);    }
        }
        @keyframes iconPing {
          0%   { transform: scale(1);   opacity: 1;   }
          70%  { transform: scale(1.7); opacity: 0;   }
          100% { transform: scale(1.7); opacity: 0;   }
        }
        @keyframes shimmer {
          0%   { background-position: -400px 0; }
          100% { background-position:  400px 0; }
        }
      `}</style>

        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            background: bg,
            color: "#fff",
            fontWeight: 500,
            fontSize: "14px",
            padding: "14px 18px 20px",   // extra bottom for progress bar
            borderRadius: "14px",
            boxShadow: `0 8px 32px ${glow}, 0 2px 8px rgba(0,0,0,0.12)`,
            minWidth: "340px",
            maxWidth: "460px",
            overflow: "hidden",
            animation: visible
              ? "toastSlideIn 0.38s cubic-bezier(0.34,1.56,0.64,1) forwards, toastPop 0.5s 0.35s ease forwards"
              : "none",
            // Shimmer overlay
            backgroundSize: "200% 100%",
          }}
        >
          {/* Shimmer sweep on entry */}
          <div style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)",
            backgroundSize: "400px 100%",
            animation: "shimmer 0.7s 0.1s ease forwards",
            pointerEvents: "none",
            borderRadius: "14px",
          }} />

          {/* Icon with ping ring */}
          <div style={{ position: "relative", flexShrink: 0 }}>
            {/* Ping ring */}
            <div style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.3)",
              animation: "iconPing 0.8s 0.2s ease-out forwards",
            }} />
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.2)",
              backdropFilter: "blur(4px)",
              flexShrink: 0,
            }}>
              {icon}
            </div>
          </div>

          {/* Text block */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: "13px", opacity: 0.85, marginBottom: "2px", letterSpacing: "0.04em", textTransform: "uppercase" }}>
              {label}
            </div>
            <div style={{ fontSize: "14px", lineHeight: "1.45", fontWeight: 400 }}>
              {message}
            </div>
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.15)",
              border: "none",
              cursor: "pointer",
              color: "#fff",
              padding: "4px",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              transition: "background 0.15s",
            }}
            onMouseEnter={e => (e.currentTarget.style.background = "rgba(255,255,255,0.28)")}
            onMouseLeave={e => (e.currentTarget.style.background = "rgba(255,255,255,0.15)")}
            aria-label="Close"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          {/* Progress bar */}
          <div style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            height: "4px",
            width: `${progress}%`,
            background: "rgba(255,255,255,0.6)",
            borderRadius: "0 2px 0 14px",
            transition: "width 0.08s linear",
            boxShadow: "0 0 6px rgba(255,255,255,0.5)",
          }} />
        </div>
      </>
    );
  };
  if (domainStatus === "ready") {
    return (
      <ThemeProvider siteContext={siteContext}>
        <NativeStatusBarSync />
        {alertMsg.open && (
          <div className="fixed left-1/2 top-6 z-[1400] -translate-x-1/2">
            <ToastContent
              message={alertMsg.message}
              severity={alertMsg.severity}
              onClose={handleClose}
            />
          </div>
        )}



        {showRoleChoiceModal && (
        <div className="fixed inset-0 z-[1300] bg-black/40" role="dialog" aria-modal="true" aria-label="Continue as">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[92%] max-w-[400px] bg-white rounded-[20px] p-8 border border-gray-200 overflow-hidden outline-none">

            {/* Decorative orbs */}
            <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full bg-blue-500/10 pointer-events-none" />
            <div className="absolute -bottom-8 -left-8 w-24 h-24 rounded-full bg-indigo-400/10 pointer-events-none" />

            {/* Header */}
            <div className="flex flex-col items-center gap-1.5 mb-6 relative">
              <div className="w-12 h-12 rounded-[14px] bg-indigo-600 flex items-center justify-center mb-1">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-gray-900 tracking-tight">Continue as</h3>
              <p className="text-[13px] text-gray-500 text-center leading-relaxed max-w-[280px]">
                Your account has both user and admin access. Choose how you'd like to proceed.
              </p>
            </div>

            {/* Role Cards */}
            <div className="flex flex-col gap-2.5 relative">

              {/* Customer */}
              <div
                onClick={() => handleRoleChoice(currentDomainUserRole)}
                className="flex items-center gap-3.5 p-3.5 rounded-[14px] border-[1.5px] border-blue-500 bg-blue-500/5 cursor-pointer transition-all duration-150 hover:-translate-y-px hover:bg-blue-500/10 active:scale-[0.98]"
              >
                <div className="w-[38px] h-[38px] rounded-[10px] bg-blue-500/10 flex items-center justify-center shrink-0">
                  <svg className="w-[18px] h-[18px] text-blue-500" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 3v18" /><path d="M13 8h2" /><path d="M13 12h2" /><path d="M13 16h2" /><path d="M5 8h2" /><path d="M5 12h2" /><path d="M5 16h2" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 mb-0.5">{currentDomainUserLabel}</p>
                  <p className="text-xs text-gray-500">{currentDomainUserDescription}</p>
                </div>
                <svg className="w-4 h-4 text-blue-500 opacity-70 shrink-0" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </div>

              {/* Admin */}
              <div
                onClick={() => handleRoleChoice("ADMIN")}
                className="flex items-center gap-3.5 p-3.5 rounded-[14px] border-[1.5px] border-gray-200 bg-transparent cursor-pointer transition-all duration-150 hover:-translate-y-px hover:border-indigo-400 hover:bg-indigo-400/10 active:scale-[0.98]"
              >
                <div className="w-[38px] h-[38px] rounded-[10px] bg-indigo-400/10 flex items-center justify-center shrink-0">
                  <svg className="w-[18px] h-[18px] text-indigo-500" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 mb-0.5">Admin</p>
                  <p className="text-xs text-gray-500">Manage users and system settings</p>
                </div>
                <svg className="w-4 h-4 text-indigo-500 opacity-50 shrink-0" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </div>
            </div>

            {/* Footer hint */}
            <p className="mt-4 text-[11px] text-gray-400 text-center">
              You can switch roles any time from your profile
            </p>
          </div>
        </div>
        )}

        {showKioskEntryPrompt && (
          <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true" aria-label="Enter Kiosk Mode">
            <form
              onSubmit={handleKioskEntrySubmit}
              className="w-full max-w-xs rounded-[20px] border border-gray-200 bg-white p-6 text-left shadow-xl"
            >
              <h3 className="text-lg font-medium text-gray-900">Enter Kiosk Mode</h3>
              <p className="mt-1 text-sm text-gray-500">Enter the PIN to switch this device into kiosk mode.</p>
              <input
                type="password"
                inputMode="numeric"
                autoFocus
                value={kioskEntryPin}
                onChange={(e) => { setKioskEntryPin(e.target.value.replace(/\D/g, "").slice(0, 8)); setKioskEntryError(""); }}
                className="mt-4 w-full rounded-xl border border-gray-200 px-4 py-3 text-center text-lg tracking-widest"
                placeholder="••••"
              />
              {kioskEntryError && (
                <p className="mt-2 text-sm font-medium text-red-600" role="alert">{kioskEntryError}</p>
              )}
              <div className="mt-5 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowKioskEntryPrompt(false)}
                  className="flex-1 rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 rounded-xl bg-gray-900 px-4 py-3 text-sm font-semibold text-white"
                >
                  Confirm
                </button>
              </div>
            </form>
          </div>
        )}

        <UserProvider value={userData}>
          <SiteContextProvider value={siteContext}>
            <CartProvider>
              <MainRoutes siteContext={siteContext} />
            </CartProvider>
          </SiteContextProvider>
        </UserProvider>
      </ThemeProvider>
    );
  }

  return <DomainLoading />;
}

export default App;
